import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { TicketSource } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { Ticket } from '~hooks/useTickets/models';
import DuplicateTicketIndicator from '~pages/Approvals/ApprovalsComponents/DuplicateTicketIndicator';
import { TicketExportIndicator } from '~pages/Approvals/ApprovalsComponents/TicketExport';
import TicketSourceBadge from '~pages/Dispatch/components/tickets-table/TicketSourceBadge';
import { Nullable } from '~types/Nullable';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';

const IMAGE_SIZE_IN_PX = 50;

interface PreviewColumnProps {
  onPotentialDuplicateChange?: (potentialDuplicate: boolean) => Promise<void>;
  showExportIndicator?: boolean;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function PreviewColumn({
  onPotentialDuplicateChange,
  showExportIndicator = false,
  sx,
  ticket,
}: PreviewColumnProps) {
  const { previewUrl, imageUrl, ticketNumber, source } = ticket ?? {};

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {previewUrl && (
        <>
          <AttachmentImage url={previewUrl} ticketSource={source} />

          <Box sx={{ overflow: 'hidden' }}>
            <PseudoLink
              action={(event?: React.MouseEvent) => {
                event?.stopPropagation();
                window.open(imageUrl, '_blank', 'noopener,noreferrer');
              }}
              title={ticketNumber || t('approvals.ticket_details.view_image')}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            />
            {ticket && (
              <DuplicateTicketIndicator
                onPotentialDuplicateChange={onPotentialDuplicateChange}
                ticket={ticket}
              />
            )}
            {showExportIndicator && ticket && <TicketExportIndicator ticket={ticket} />}
          </Box>
        </>
      )}

      {ticketNumber && !previewUrl && (
        <Box sx={{ overflow: 'hidden' }}>
          <OverflowAwareText>{ticketNumber}</OverflowAwareText>
          {ticket && (
            <DuplicateTicketIndicator
              onPotentialDuplicateChange={onPotentialDuplicateChange}
              ticket={ticket}
            />
          )}
          {showExportIndicator && ticket && <TicketExportIndicator ticket={ticket} />}
        </Box>
      )}

      {showExportIndicator && ticket && !previewUrl && !ticketNumber && (
        <TicketExportIndicator ticket={ticket} />
      )}
    </Box>
  );
}

interface AttachmentImageProps {
  ticketSource: Nullable<TicketSource>;
  url: string;
}

function AttachmentImage({ url, ticketSource }: AttachmentImageProps) {
  return (
    <Box
      sx={(theme) => ({
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 1,
        display: 'flex',
        flex: 0,
        height: `${IMAGE_SIZE_IN_PX}px`,
        minWidth: `${IMAGE_SIZE_IN_PX}px`,
        overflow: 'hidden',
        position: 'relative',
      })}
    >
      {ticketSource && <TicketSourceBadge ticketSource={ticketSource} />}

      <Box
        component="img"
        alt="ticket-preview"
        crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
        src={url}
        sx={{
          display: 'block',
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
        }}
      />
    </Box>
  );
}
